// ============================================================
// RailTooltip.tsx
// Casevo-styled hover tooltip for the collapsed sidebar rail (replaces
// the browser's native `title` tooltip there).
//
// Wraps a rail item; on hover / keyboard focus it shows a small dark
// forest label to the RIGHT of the item, vertically centred on it.
// Rendered through a portal with position:fixed so it escapes the
// sidebar's overflow-x-hidden scroll region (an absolutely-positioned
// child would be clipped at the rail's right edge). placement="below"
// centres it under the item instead (the required-fields chips, the
// top-bar bell); "below-end" right-aligns it with the item so it can't
// run off the viewport (the top-bar avatar at the right edge).
//
// `enabled` lets the same wrapper sit around nav rows in both states —
// the tooltip only appears when the rail is collapsed (in expanded mode
// the full label is already visible, so no tooltip).
//
// (Big-audit fix batch 1) `wrap` lets a longer explanation wrap over a few
// lines (the e-sign evidence-pack buttons) instead of being cut off; `as`
// renders the wrapper as a <span> inside inline content; and a "below"
// tooltip is nudged back inside the viewport (16px gutter) when it would run
// off an edge — the arrow still points at the item.
// ============================================================
import React, { ReactNode, useCallback, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

interface RailTooltipProps {
  /** Main line (e.g. the item's name). */
  label: string
  /** Optional muted second line (e.g. "Case", "FAEC86B1"). */
  sublabel?: string
  /** Only show when true (i.e. the sidebar is collapsed). */
  enabled: boolean
  /** 'right' (the rail, default), 'below' — centred under the item (inline chips),
   *  or 'below-end' — under the item, right edges aligned (items at the viewport's right edge). */
  placement?: 'right' | 'below' | 'below-end'
  /** Below placements only: start under the nearest ancestor matching this
   *  selector instead of under the item (the top bar passes 'nav' so the
   *  tooltip, pointer included, sits clear of the header). */
  belowEdgeOf?: string
  /** Extra classes for the wrapper (e.g. 'inline-flex' in a chip row). */
  className?: string
  /** Let the label wrap over several lines (a sentence), up to 260px wide. */
  wrap?: boolean
  /** The wrapper element ('span' inside inline content). */
  as?: 'div' | 'span'
  children: ReactNode
}

const GUTTER = 16

const RailTooltip: React.FC<RailTooltipProps> = ({ label, sublabel, enabled, placement = 'right', belowEdgeOf, className, wrap = false, as = 'div', children }) => {
  // Anchor position (viewport coords) while visible; null = hidden.
  // `right` is set only for 'below-end' (distance from the viewport's right
  // edge), with `arrow` = the arrow's offset from the tooltip's right edge
  // so it still points at the item's centre.
  const [pos, setPos] = useState<{ left?: number; right?: number; top: number; arrow?: number } | null>(null)
  const below = placement !== 'right'
  const end = placement === 'below-end'

  const show = useCallback((el: HTMLElement) => {
    if (!enabled) return
    const r = el.getBoundingClientRect()
    const edge = belowEdgeOf ? el.closest(belowEdgeOf) : null
    const top = (edge ? edge.getBoundingClientRect().bottom : r.bottom) + 8
    if (end) setPos({ right: document.documentElement.clientWidth - r.right, top, arrow: r.width / 2 })
    else setPos(below ? { left: r.left + r.width / 2, top } : { left: r.right + 10, top: r.top + r.height / 2 })
  }, [enabled, below, end, belowEdgeOf])
  const hide = useCallback(() => setPos(null), [])
  // A centred "below" tooltip that would cross the viewport's edge is shifted
  // back in (measured once drawn, before paint); the arrow moves the other way.
  const tipRef = useRef<HTMLDivElement>(null)
  const [shift, setShift] = useState(0)
  useLayoutEffect(() => {
    const el = tipRef.current
    if (!pos || placement !== 'below' || !el) { setShift(0); return }
    const r = el.getBoundingClientRect()
    if (!r.width) { setShift(0); return }
    const vw = document.documentElement.clientWidth
    const left = r.left - shift
    const right = left + r.width
    setShift(left < GUTTER ? GUTTER - left : right > vw - GUTTER ? Math.max(GUTTER - left, vw - GUTTER - right) : 0)
    // `shift` is read, not a trigger: re-measure only when the tooltip moves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pos, placement])
  const Wrapper = as

  return (
    <Wrapper
      className={className}
      onMouseEnter={(e) => show(e.currentTarget)}
      onMouseLeave={hide}
      onFocus={(e) => show(e.currentTarget)}
      onBlur={hide}
      // Clicking navigates / changes layout — drop the tooltip so it
      // never lingers over the new screen.
      onClick={hide}
      // (Audit fix batch 2, Low 12) Escape dismisses it (WCAG 1.4.13) — without
      // moving focus; the tooltip only comes back on the next hover / focus.
      onKeyDown={(e) => { if (e.key === 'Escape' && pos) { e.stopPropagation(); hide() } }}
    >
      {children}
      {enabled && pos && createPortal(
        <div
          role="tooltip"
          ref={tipRef}
          style={{ left: pos.left !== undefined ? pos.left + shift : undefined, right: pos.right, top: pos.top }}
          // Short fade-in (opacity only, so it doesn't fight the translate).
          className={`fixed z-[300] ${end ? '' : below ? '-translate-x-1/2' : '-translate-y-1/2'} pointer-events-none font-sans
                     rounded-md bg-casevo-tooltip px-2.5 py-1.5 shadow-lg ring-1 ring-white/10
                     ${wrap ? 'w-max max-w-[min(260px,calc(100vw-32px))]' : 'max-w-[260px]'} animate-[fade-in_120ms_ease-out_1] motion-reduce:animate-none`}
        >
          {/* Small arrow pointing back at the item. */}
          <span
            aria-hidden="true"
            style={end ? { right: (pos.arrow ?? 0) - 4 } : shift ? { marginLeft: -shift } : undefined}
            className={`absolute rotate-45 w-2 h-2 bg-casevo-tooltip ${end ? 'top-0 -translate-y-1/2' : below ? 'left-1/2 top-0 -translate-x-1/2 -translate-y-1/2' : 'left-0 top-1/2 -translate-x-1/2 -translate-y-1/2'}`}
          />
          <span className={`relative block text-[12px] font-medium text-white ${wrap ? 'whitespace-normal leading-[1.4]' : 'whitespace-nowrap truncate'}`}>
            {label}
          </span>
          {sublabel && (
            <span className="relative block text-[11px] text-[#74c69d] uppercase tracking-wider whitespace-nowrap truncate">
              {sublabel}
            </span>
          )}
        </div>,
        document.body,
      )}
    </Wrapper>
  )
}

export default RailTooltip
